import React from 'react';
import { NavLink, useNavigate, Outlet } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { 
  Heart, 
  ArrowLeft,
  Shield,
  Building2,
  Bell,
  Users,
  Database,
  Receipt,
  Palette,
  Globe,
  Key,
  Package,
  HardDrive,
  Layout
} from 'lucide-react';
import { Button } from '../ui/button';

const SettingsLayout = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isSuperAdmin = user?.role === 'super_admin';

  const settingsNavItems = [
    { to: '/settings', icon: Shield, label: 'System', roles: ['super_admin'], exact: true },
    { to: '/settings/organization', icon: Building2, label: 'Organization', roles: ['super_admin', 'admin'] },
    { to: '/settings/products-services', icon: Package, label: 'Products & Services', roles: ['super_admin'] },
    { to: '/settings/storage', icon: HardDrive, label: 'Storage', roles: ['super_admin'] },
    { to: '/settings/templates', icon: Layout, label: 'Form Templates', roles: ['super_admin'] },
    { to: '/settings/notifications', icon: Bell, label: 'Notifications', roles: ['super_admin', 'admin', 'team', 'client'] },
  ];

  const filteredNavItems = settingsNavItems.filter(item => item.roles.includes(user?.role));

  return (
    <div className="flex min-h-[calc(100vh-64px)]" data-testid="settings-layout">
      {/* Settings Sidebar */}
      <aside className="w-64 bg-white border-r border-slate-200 flex-shrink-0 hidden lg:block">
        <div className="p-4 border-b border-slate-100">
          <Button 
            variant="ghost" 
            className="w-full justify-start text-slate-600 hover:text-slate-900"
            onClick={() => navigate('/dashboard')}
            data-testid="back-to-dashboard-btn"
          >
            <ArrowLeft size={18} className="mr-2" />
            Back to Dashboard
          </Button>
        </div>
        
        <div className="p-4">
          <h2 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">
            Settings
          </h2>
          <nav className="space-y-1">
            {filteredNavItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.exact}
                className={({ isActive }) => 
                  `flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                    isActive 
                      ? 'bg-slate-900 text-white' 
                      : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
                  }`
                }
                data-testid={`settings-nav-${item.label.toLowerCase().replace(' ', '-')}`}
              >
                <item.icon size={18} />
                <span>{item.label}</span>
              </NavLink>
            ))}
          </nav>
        </div>

        {/* App Info */}
        <div className="absolute bottom-0 left-0 w-64 p-4 border-t border-slate-100 bg-white">
          <div className="flex items-center gap-2 text-slate-500">
            <Heart size={16} className="fill-slate-500" />
            <span className="text-xs">My Client PM v1.0</span>
          </div>
        </div>
      </aside>

      {/* Settings Content */}
      <main className="flex-1 p-6 lg:p-8 bg-slate-50 overflow-auto">
        <Outlet />
      </main>
    </div>
  );
};

export default SettingsLayout;
